import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useStripeSetUp from './useStripeSetup';
import { isReactNativeWebView, isSudoManager } from '../../utils/global/helper';
import useUser from '../global/useUser';
import useGetOnboarding from '../businesses/useGetOnboarding';

const KEY = 'stripe-account-banner-visible';

function useStripeAccountBanner() {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { completedSteps } = useGetOnboarding();

  const isUserSudoManager = !!user && isSudoManager(user);
  const isPaymentsOnboardingComplete = !!completedSteps?.some(
    step => step.id === 'PAYMENTS',
  );

  const { data, onViewStripeAccount } = useStripeSetUp({
    enabled:
      user?.business?.type === 'STUDIO' &&
      isUserSudoManager &&
      isPaymentsOnboardingComplete,
  });
  const { isOnboardComplete, isDisabled, isIncomplete } = data || {};

  const { data: isVisible = true } = useQuery({
    queryKey: [KEY],
    queryFn: () => {
      const value = sessionStorage.getItem(KEY);
      return !value || value === 'true';
    },
  });

  const hideBanner = () => {
    queryClient.setQueryData([KEY], false);
    sessionStorage.setItem(KEY, 'false');
  };

  const isBannerShown = React.useMemo(
    () =>
      user?.business?.type === 'STUDIO' &&
      isPaymentsOnboardingComplete &&
      !!isOnboardComplete &&
      !!(isDisabled || isIncomplete) &&
      isUserSudoManager,
    [
      user?.business?.type,
      isPaymentsOnboardingComplete,
      isOnboardComplete,
      isDisabled,
      isIncomplete,
      isUserSudoManager,
    ],
  );

  return {
    isBannerShown: Boolean(
      isBannerShown && isVisible && !isReactNativeWebView(),
    ),
    hideBanner,
    onViewStripeAccount,
  };
}

export default useStripeAccountBanner;
